import React, { useState, useMemo, useEffect } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown, Search, X } from "lucide-react";

// value encoding: ""(all) | "A,B" (include) | "!A,B" (exclude)
function parse(value) {
  if (!value) return { mode: "include", set: new Set() };
  const exclude = value.startsWith("!");
  const body = exclude ? value.slice(1) : value;
  return { mode: exclude ? "exclude" : "include", set: new Set(body.split("||").filter(Boolean)) };
}
function encode(mode, set) {
  if (!set.size) return "";
  return (mode === "exclude" ? "!" : "") + [...set].join("||");
}

export default function MultiFilter({ label, value, onChange, options, render, testId }) {
  const parsed = parse(value);
  const set = parsed.set;
  const [mode, setModeState] = useState(parsed.mode);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (parse(value).set.size) setModeState(parse(value).mode);
  }, [value]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? options.filter((o) => String(o).toLowerCase().includes(t) || (render && render(o).toLowerCase().includes(t))) : options;
  }, [q, options, render]);

  const toggle = (o) => {
    const next = new Set(set);
    next.has(o) ? next.delete(o) : next.add(o);
    onChange(encode(mode, next));
  };
  const setMode = (m) => { setModeState(m); onChange(encode(m, set)); };
  const clear = () => onChange("");

  const summary = !set.size ? "All" : `${mode === "exclude" ? "Excl " : ""}${set.size} selected`;

  return (
    <div className="min-w-[150px]">
      <label className="label-eyebrow block mb-1">{label}</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button data-testid={testId}
            className={`h-9 w-full px-3 rounded-md border text-[13px] bg-white flex items-center justify-between gap-2 ${set.size ? "border-slate-400 text-slate-900 font-medium" : "border-[var(--line)] text-slate-500"}`}>
            <span className="truncate">{summary}</span>
            <ChevronDown className="w-4 h-4 shrink-0 opacity-60" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0" align="start">
          <div className="p-2 border-b border-[var(--line)]">
            <div className="flex rounded-md bg-slate-100 p-0.5 mb-2">
              {[["include", "Include"], ["exclude", "Exclude"]].map(([m, l]) => (
                <button key={m} onClick={() => setMode(m)} data-testid={`${testId}-mode-${m}`}
                  className={`flex-1 h-7 rounded text-[12px] font-medium ${mode === m ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>{l}</button>
              ))}
            </div>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
                className="w-full h-8 pl-8 pr-2 rounded border border-[var(--line)] text-[12px] outline-none focus:border-slate-400" />
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto slim py-1">
            {filtered.length === 0 && <div className="px-3 py-3 text-[12px] text-slate-400">No matches</div>}
            {filtered.map((o) => (
              <div key={o} role="option" aria-selected={set.has(o)} onClick={() => toggle(o)} data-testid={`${testId}-opt-${o}`}
                className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 text-left cursor-pointer">
                <Checkbox checked={set.has(o)} className="pointer-events-none" />
                <span className="text-[12.5px] text-slate-700 truncate">{render ? render(o) : o}</span>
              </div>
            ))}
          </div>
          {set.size > 0 && (
            <div className="p-2 border-t border-[var(--line)] flex items-center justify-between">
              <span className="text-[11px] text-slate-400">{set.size} {mode === "exclude" ? "excluded" : "selected"}</span>
              <button onClick={clear} className="text-[12px] text-slate-600 hover:text-slate-900 flex items-center gap-1" data-testid={`${testId}-clear`}><X className="w-3 h-3" />Clear</button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
